
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './styles.module.scss';

import { animate, motion, useDragControls, useMotionValue } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faChevronUp, faFilter, faGripLines, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import MapWithListingData from '../../../Misc/MapWithListingData/MapWithListingData';
import Head from 'next/head';
import errorMsg from '../../../Functions/errorMsg';
import axios from 'axios';

import { ScaleLoader } from 'react-spinners';

import Filter from '../../../Filters/Filter';

import DisplayCard from './DisplayCard/DisplayCard';

export default function Vendors() {
  const router = useRouter();
  
  // Loading state variables
  const [mapLoading, setMapLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  const [listingData, setListingData] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [showingData, setShowingData] = useState([]);
  const [availableObjects, setAvailableObjects] = useState(null);

  // Fetch listing data
  useEffect(() => {
    if (!dataLoading || listingData) return;

    async function getListingData() {
      const result = await axios
        .get(`${window.location.origin}/api/vendor/general/vendorListings`)
        .catch((error) => errorMsg(error?.response?.data?.status || error.toString()));

      if (result && result.status === 200) {
        const data = result.data;
        setListingData(data.listings || []);
        console.log(data.listings);
        setAvailableObjects(data.availableObjects || []);
      }
    }

    getListingData();
    setDataLoading(false);
  }, [dataLoading, listingData, availableObjects]);

  // Map Interaction Click Handler
  const [searchText, setSearchText] = useState('');
  const [selectedObjects, setSelectedObjects] = useState([]);

  useEffect(() => {
    if (!listingData || listingData.length === 0) return;

    const filteredListings = searchText.length > 0
      ? listingData.filter((listing) => {
          const searchTextCheck = searchText.trim().toLowerCase();
          return (
            listing.displayName.toLowerCase().includes(searchTextCheck) ||
            listing.category.toLowerCase().includes(searchTextCheck)
          );
        })
      : listingData.filter((listing) => selectedObjects.includes(listing.zip));

    setSelectedData(filteredListings);
    setShowingData(filteredListings);
  }, [selectedObjects, listingData, searchText]);

  function addZipToQuery(zip) {
    const currentZips = Array.isArray(router.query.zipCodesSelected)
      ? router.query.zipCodesSelected
      : router.query.zipCodesSelected
      ? [router.query.zipCodesSelected]
      : [];
  
    // Prevent clearing the query accidentally
    const updatedZips = [...new Set([...currentZips, zip])]; // Avoid duplicates
  
    router.push(
      {
        query: {
          ...router.query,
          zipCodesSelected: updatedZips.length === 1 ? updatedZips[0] : updatedZips, // Handle single or multiple ZIPs
        },
      },
      undefined,
      { shallow: true }
    );
  }
  
  
  function removeZipFromQuery(zip) {
    const currentZips = Array.isArray(router.query.zipCodesSelected)
      ? router.query.zipCodesSelected
      : router.query.zipCodesSelected
      ? [router.query.zipCodesSelected]
      : [];
  
    const updatedZips = currentZips.filter((z) => z !== zip);
  
    router.push(
      {
        query: {
          ...router.query,
          zipCodesSelected: updatedZips.length === 1 ? updatedZips[0] : updatedZips, // Handle single or multiple ZIPs
        },
      },
      undefined,
      { shallow: true }
    );
  }
  
  function handleObjectClick(zip) {
    setSelectedObjects((prevObjects) => {
      if (prevObjects.includes(zip)) {
        removeZipFromQuery(zip);
        return prevObjects.filter((curObj) => curObj !== zip);
      } else {
        addZipToQuery(zip);
        return [...prevObjects, zip];
      }
    });
  }
  
  // Sync selectedObjects with query parameters on initial load
  useEffect(() => {
    if (router.query.zipCodesSelected) {
      const zips = Array.isArray(router.query.zipCodesSelected)
        ? router.query.zipCodesSelected
        : [router.query.zipCodesSelected];
      setSelectedObjects(zips); // Sync state with query
    }
  }, [router.query.zipCodesSelected]);
  
  

  function isLoading() {
    return mapLoading || dataLoading;
  }

  const [filterBar, setFilterBar] = useState(false);

  const showFilterBar = () => {
    if (isLoading()) return;
    console.log("HI");
    setFilterBar((prevFilterBar) => !prevFilterBar);
  };

  function filterApplyArray(newArray) {
    setShowingData(newArray);
  }

  function clearFilter() {
    setShowingData(selectedData);
  }

  const memoizeListingContainer = useMemo(() => (
    <ListingContainer
      noListings={listingData?.length === 0}
      listings={showingData}
      showFilterBar={showFilterBar}
      handleSearchMethod={(text) => setSearchText(text)}
      noResults={selectedData.length > 0 && showingData === 0}
    />
  ), [showingData, listingData]);

  const memoizeMap = useMemo(() => (
    <MapWithListingData
      type="ZIP"
      availableObjects={availableObjects}
      listingData={listingData}
      handleMapLoaded={() => setMapLoading(false)}
      handleClick={handleObjectClick}
    />
  ), [listingData, availableObjects]);
  
  return (
    <section id={styles.wrapper}>
      <Filter
        isLoading={isLoading()}
        filterType="Vendors"
        unfilteredData={selectedData}
        allResults={showingData}
        filterBar={filterBar}
        showFilterBar={showFilterBar}
        filterApplyArray={filterApplyArray}
        clearFilter={clearFilter}
      />
      <Head>
        <title>Waco Local Vendors Marketplace</title>
        <meta name="description" content="Waco Exchange is proud to announce its Vendor Program, allowing locals to browse and post local distributors of services and products at absolutely no cost." />
      </Head>
      {isLoading() && (
        <div id={styles.loading}>
          <ScaleLoader width={50} height={40} color="white" />
        </div>
      )}
      <div id={styles.controls}></div>
      <section id={styles.content}>
        {listingData && <h1 id={styles.mapTypeText}>Vendor Map</h1>}
        {listingData && memoizeMap}
        {listingData && memoizeListingContainer}
      </section>
    </section>
  );
}


const ListingContainer = ({ listings, showFilterBar, handleSearchMethod, noListings, noResults }) => {
  const [showDisplayed, setShowDisplayed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const controls = useDragControls();
  const y = useMotionValue(showDisplayed ? 0 : 400);

  const totalPages = Math.ceil(listings.length / itemsPerPage);

  const displayContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const [showToTop, setShowToTop] = useState(false);

  // Handle Pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const currentListings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return listings.slice(startIndex, startIndex + itemsPerPage);
  }, [listings, currentPage]);

  // Handle Drag Animation
  const onClickHandle = () => {
    const targetY = showDisplayed ? 400 : 0;
    setShowDisplayed(!showDisplayed);
    animate(y, targetY, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
  };

  const onDragEnd = (_, info) => {
    const velocity = info.velocity.y;
    const targetY = velocity > 0 ? 400 : 0;
    setShowDisplayed(velocity <= 0);
    animate(y, targetY, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
  };

  // Handle Scroll
  const handleScroll = () => {
    if (!displayContainerRef.current) return;
    const position = displayContainerRef.current.scrollTop;
    setShowToTop(position > 200);
  };

  const handleScrollUp = (e) => {
    e.preventDefault();
    if (displayContainerRef.current) {
      displayContainerRef.current.scroll({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const container = displayContainerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });
    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    const text = searchInputRef.current?.value || '';
    handleSearchMethod(text);
  };

  return (
    <motion.section
      id={styles.viewListings}
      style={{ y }}
      drag="y"
      dragElastic={0}
      dragConstraints={{ top: 0, bottom: 400 }}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
    >
      <div>
        <FontAwesomeIcon
          onClick={onClickHandle}
          icon={faGripLines}
          id={styles.handleContainerIcon}
          size="lg"
          onPointerDown={(e) => controls.start(e)}
        />
      </div>

      <div ref={displayContainerRef} id={styles.displayContainer}>
        {showToTop && (
          <div id={styles.toTop}>
            <FontAwesomeIcon onClick={handleScrollUp} icon={faChevronUp} id={styles.icon} />
          </div>
        )}

        <section id={styles.queryMethods}>
          <div>
            <input ref={searchInputRef} placeholder="Search..." />
            <div onClick={handleSearch}>
              <FontAwesomeIcon icon={faMagnifyingGlass} id={styles.searchButton} />
            </div>
          </div>
          <div>
            <FontAwesomeIcon icon={faFilter} id={styles.filter} onClick={showFilterBar} />
          </div>
          <div>
            <FontAwesomeIcon
              icon={faChevronLeft}
              id={styles.pageNav}
              onClick={handlePreviousPage}
              style={{ cursor: currentPage > 1 ? 'pointer' : 'not-allowed' }}
            />
            <h1>
              {currentPage} / {totalPages || 1}
            </h1>
            <FontAwesomeIcon
              icon={faChevronRight}
              id={styles.pageNav}
              onClick={handleNextPage}
              style={{ cursor: currentPage < totalPages ? 'pointer' : 'not-allowed' }}
            />
          </div>
        </section>

        {listings.length > 0 && (
          <section id={styles.cardContainer}>
            {currentListings.map((listing, index) => (
              <DisplayCard key={index} listing={listing} />
            ))}
          </section>
        )}

        {noListings && listings.length === 0 && !noResults && (
          <section
            id={styles.cardContainer}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <h1 id={styles.noListings}>No Listings</h1>
          </section>
        )}

        {noResults && (
          <section
            id={styles.cardContainer}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <h1 id={styles.noResults}>No Results</h1>
          </section>
        )}
      </div>
    </motion.section>
  );
};
